/**
 * LCFS FSE Registry — the canonical roster of Fuelling Supply Equipment.
 *
 * An FSE ID is the identity CARB validates a quarterly fuel transaction row
 * against, so a wrong one invalidates the row. This screen is the one place
 * that identity lives: one record per dispenser, with every alias the source
 * documents use, so a document naming "TTF RTL Marina CA" and one naming
 * "Trillium - MRWMD" resolve to the same equipment.
 *
 * There is deliberately no reconciliation view here. An earlier version
 * compared this roster against the identifiers in the hand-maintained
 * allocation spreadsheets and classified how they had drifted. That drift was
 * an artifact of the spreadsheet process this platform replaces: once
 * identifiers come from one registry and outputs are generated rather than
 * typed, the drift cannot occur, and a screen that detects an impossible error
 * is maintenance with no payoff. The durable safeguard is validation at the
 * point of entry — check an FSE ID is well-formed and known to CARB when it is
 * added — not comparison after the fact.
 *
 * Backend: frontend-first. `GET /lcfs/fse` is served by MSW (see
 * `mocks/handlers.ts`, `VITE_USE_LCFS_MSW`).
 */

import {
  Alert,
  Badge,
  Group,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconAlertTriangle } from "@tabler/icons-react";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useFseRegistry } from "../api/hooks";
import type { FseRecord } from "../api/types";
import { useBranding } from "../auth/useBranding";
import { DairyHistoryDrawer } from "../components/lcfs/DairyHistoryDrawer";
import { StationHistoryDrawer } from "../components/lcfs/StationHistoryDrawer";
import { useLcfsDrilldown } from "../components/lcfs/useLcfsDrilldown";
import { formatApiError } from "../lib/errors";

export function FseRegistryPage() {
  const { dispensing_partner_label: partner } = useBranding();
  // Filters are state-of-record in the URL (locked convention) so a filtered
  // roster survives reload and is shareable.
  const [params, setParams] = useSearchParams();
  const dispenser = params.get("dispenser") ?? "";
  const utility = params.get("utility") ?? "";

  const setParam = (key: string, value: string) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  const registryQ = useFseRegistry();
  const records = registryQ.data?.items ?? [];
  const drill = useLcfsDrilldown();

  const dispensers = useMemo(
    () => Array.from(new Set(records.map((r) => r.dispenser).filter(Boolean))).sort(),
    [records],
  );
  const utilities = useMemo(
    () => Array.from(new Set(records.map((r) => r.utility).filter((u): u is string => !!u))).sort(),
    [records],
  );

  const filtered = records.filter(
    (r) => (!dispenser || r.dispenser === dispenser) && (!utility || r.utility === utility),
  );

  /** Stations still missing the dispensing partner's identifier. */
  const missingTrillium = records.filter((r) => r.trillium_fse_id === null).length;
  /** Addresses a person should look at — a formatter cannot settle these. */
  const needsReview = records.filter((r) => r.address_review !== null).length;

  return (
    <Stack gap="lg">
      {/* No section heading: the tab label is it — see `routes/Lcfs.tsx`. */}
      <Stack gap={4}>
        <Text c="dimmed" size="sm" maw={820}>
          One record per Fuelling Supply Equipment, straight from the station schedule, holding all
          three identifiers the same dispenser carries. The FSE ID is the calculation key and the
          only one valid on a CARB quarterly fuel transaction row; the dispensing partner reports
          throughput under its own, and CARB tracks the site under an LFF ID. A working document
          keyed on {partner}&apos;s identifiers is correct for its purpose — this roster translates
          between them.
        </Text>
      </Stack>

      <Paper p="md" radius="md" withBorder>
        <Group gap="md" wrap="wrap" align="flex-end">
          <Select
            label="Dispenser"
            data={[{ value: "", label: "All dispensers" }, ...dispensers]}
            value={dispenser}
            onChange={(v) => setParam("dispenser", v ?? "")}
            w={190}
            allowDeselect={false}
          />
          <Select
            label="Utility"
            data={[{ value: "", label: "All utilities" }, ...utilities]}
            value={utility}
            onChange={(v) => setParam("utility", v ?? "")}
            w={190}
            allowDeselect={false}
          />
          <Text size="sm" c="dimmed">
            {filtered.length} of {records.length} registered
            {missingTrillium > 0 ? ` · ${missingTrillium} awaiting a partner identifier` : ""}
            {needsReview > 0 ? ` · ${needsReview} addresses to review` : ""}
          </Text>
        </Group>
      </Paper>

      {registryQ.isPending ? (
        <Skeleton height={420} radius="md" />
      ) : registryQ.isError ? (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">{formatApiError(registryQ.error)}</Text>
        </Alert>
      ) : (
        <RegistryTable records={filtered} onOpen={drill.openStation} />
      )}

      <StationHistoryDrawer
        fseId={drill.stationId}
        onClose={drill.closeStation}
        onOpenDairy={drill.openDairy}
      />
      <DairyHistoryDrawer
        dairyId={drill.dairyId}
        onClose={drill.closeDairy}
        onOpenStation={drill.openStation}
      />
    </Stack>
  );
}

function RegistryTable({
  records,
  onOpen,
}: {
  records: FseRecord[];
  onOpen: (fseId: string) => void;
}) {
  const { dispensing_partner_label: partner } = useBranding();
  if (records.length === 0) {
    return (
      <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">No FSEs match these filters.</Text>
      </Alert>
    );
  }

  return (
    <Paper radius="md" withBorder>
      <ScrollArea>
        <Table striped highlightOnHover verticalSpacing="sm" miw={900}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>FSE ID</Table.Th>
              <Table.Th>Station</Table.Th>
              <Table.Th>Dispenser</Table.Th>
              <Table.Th>LCFS (LFF) ID</Table.Th>
              <Table.Th>{partner} FSE ID</Table.Th>
              <Table.Th>Utility / meter</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {records.map((r) => (
              <Table.Tr
                key={r.fse_id}
                onClick={() => onOpen(r.fse_id)}
                style={{ cursor: "pointer" }}
              >
                <Table.Td>
                  <Text ff="monospace" fw={600} size="sm">
                    {r.fse_id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm">{r.station_name}</Text>
                    <Group gap={6} wrap="nowrap">
                      <Text size="xs" c="dimmed">
                        {r.address_normalized ?? "—"}
                      </Text>
                      {r.address_review ? (
                        <Tooltip label={r.address_review} multiline w={300} withArrow>
                          <ThemeIcon
                            variant="light"
                            color="yellow"
                            size={16}
                            radius="sm"
                            aria-label="Address needs review"
                          >
                            <IconAlertTriangle size={11} />
                          </ThemeIcon>
                        </Tooltip>
                      ) : null}
                    </Group>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Badge variant="default" size="sm" radius="sm">
                    {r.dispenser}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace" c="dimmed">
                    {r.lff_id ?? "—"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {r.trillium_fse_id ? (
                    <Text size="xs" ff="monospace" c="dimmed">
                      {r.trillium_fse_id}
                    </Text>
                  ) : (
                    <Tooltip
                      label="No identifier from the dispensing partner yet. The source sheet had a note typed into this field; it is stored as absent, which is what it means."
                      multiline
                      w={300}
                      withArrow
                    >
                      <Badge color="yellow" variant="light" size="sm">
                        awaited
                      </Badge>
                    </Tooltip>
                  )}
                </Table.Td>
                <Table.Td>
                  <Stack gap={0}>
                    <Text size="xs">{r.utility ?? "—"}</Text>
                    {r.meter_number ? (
                      <Text size="xs" ff="monospace" c="dimmed">
                        {r.meter_number}
                      </Text>
                    ) : null}
                  </Stack>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}
